import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Release } from "@backstory/protocol";
import { useAuth } from "../../../auth/store";
import { EmptySearchMark, ErrorState } from "../../../components/States";
import { PageHeader } from "../../../components/ui";
import { formatDateTime, formatRelative } from "../../../lib/format";
import { insightsApi, insightsKeys, type TimeRange } from "../api";
import { ms, pct } from "../charts";
import { useSlowPending, WakingNumbers } from "../WakingNumbers";
import { Card, Delta, RangePicker, useRange } from "../components";
import { rangeLabel, rangeToSearch, type RangeState } from "../range";

export interface ReleaseDelta {
  metric: string;
  a?: number;
  b?: number;
  delta: number;
  /** For error rate and vitals, lower is better. */
  upIsGood: boolean;
  format: (v: number | undefined) => string;
  suffix?: string;
  pctDelta?: boolean;
}

/** Side-by-side deltas (b minus a). Percentage deltas where the base is a count. */
export function compareReleases(a: Release, b: Release): ReleaseDelta[] {
  const rel = (x?: number, y?: number) => (x && y !== undefined ? ((y - x) / x) * 100 : NaN);
  return [
    { metric: "Sessions", a: a.sessions, b: b.sessions, delta: rel(a.sessions, b.sessions), upIsGood: true, format: (v) => (v ?? 0).toLocaleString(), suffix: "%", pctDelta: true },
    { metric: "Sessions with errors", a: a.errorRate, b: b.errorRate, delta: b.errorRate - a.errorRate, upIsGood: false, format: (v) => pct(v, 2), suffix: " pts" },
    { metric: "New issues", a: a.newClusters, b: b.newClusters, delta: b.newClusters - a.newClusters, upIsGood: false, format: (v) => String(v ?? 0) },
    { metric: "LCP p75", a: a.vitals.lcpP75, b: b.vitals.lcpP75, delta: (b.vitals.lcpP75 ?? NaN) - (a.vitals.lcpP75 ?? NaN), upIsGood: false, format: ms, suffix: " ms" },
    { metric: "INP p75", a: a.vitals.inpP75, b: b.vitals.inpP75, delta: (b.vitals.inpP75 ?? NaN) - (a.vitals.inpP75 ?? NaN), upIsGood: false, format: ms, suffix: " ms" },
    { metric: "CLS p75", a: a.vitals.clsP75, b: b.vitals.clsP75, delta: (b.vitals.clsP75 ?? NaN) - (a.vitals.clsP75 ?? NaN), upIsGood: false, format: (v) => (v === undefined ? "—" : v.toFixed(3)) },
  ];
}

/** "in the last 7 days" or "from Sep 1 to Sep 5", for a sentence about the selected period. */
export function periodPhrase(state: RangeState, range: TimeRange): string {
  if (state.preset !== "custom") return `in the ${rangeLabel(state).toLowerCase()}`;
  // Custom bounds are calendar days in UTC; showing them in local time would name the evening before.
  const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  return `from ${day(range.from)} to ${day(range.to)}`;
}

export function ReleasesPage() {
  const auth = useAuth();
  const { state, range, set: setRange } = useRange();
  const q = useQuery({ queryKey: insightsKeys.releases(range, auth.currentProjectId), queryFn: () => insightsApi.releases(range) });
  // "Latest" means the build receiving the newest session, not the build whose
  // first-ever session happened a few seconds later during a mixed rollout.
  const releases = useMemo(() => [...(q.data ?? [])].sort((x, y) => new Date(y.lastSeenAt).getTime() - new Date(x.lastSeenAt).getTime()), [q.data]);
  const [a, setA] = useState<string>("");
  const [b, setB] = useState<string>("");
  // A release that drops out of the selected period falls back to the newest
  // pair still in the list, so the comparison never points at a blank row.
  const relA = releases.find((r) => r.release === a) ?? releases[1] ?? releases[0];
  const relB = releases.find((r) => r.release === b) ?? releases[0];
  const deltas = relA && relB ? compareReleases(relA, relB) : [];
  const totalSessions = releases.reduce((n, r) => n + r.sessions, 0);
  const empty = !q.isPending && releases.length === 0;
  const slowLoad = useSlowPending(q.isPending);

  return (
    <>
      <PageHeader title="Releases" actions={<RangePicker />} />
      {q.isError ? (
        <div className="card section"><ErrorState error={q.error} retry={() => void q.refetch()} /></div>
      ) : q.isPending && slowLoad ? (
        <WakingNumbers />
      ) : empty ? (
        <div className="card empty releases-empty" data-testid="releases-empty">
          <EmptySearchMark />
          <h3>No releases in this period</h3>
          <p>No sessions were recorded for any release {periodPhrase(state, range)}.</p>
          {state.preset !== "30d" && (
            <button type="button" className="btn" onClick={() => setRange({ preset: "30d" })}>Show the last 30 days</button>
          )}
        </div>
      ) : (
        <>
          <div className="card releases-card" style={{ marginBottom: 16 }}>
            <table className="table releases-table" aria-label="Releases" aria-busy={q.isPending || undefined}>
              <thead><tr><th>Release</th><th>First seen</th><th style={{ textAlign: "right" }}>Sessions</th><th>Share</th><th style={{ textAlign: "right" }}>Sessions with errors</th><th style={{ textAlign: "right" }}>New issues</th><th style={{ textAlign: "right" }}>LCP p75</th><th style={{ textAlign: "right" }}>INP p75</th><th style={{ textAlign: "right" }}>CLS p75</th></tr></thead>
              <tbody>
                {releases.map((r) => {
                  const share = totalSessions > 0 ? (r.sessions / totalSessions) * 100 : 0;
                  return (
                  <tr key={r.release} className="static" data-testid="release-row">
                    <td><Link to="/issues" search={{ release: r.release, ...rangeToSearch(state) } as never} className="mono">{r.release}</Link></td>
                    <td className="small muted" data-label="First seen" title={formatDateTime(r.firstSeenAt)}>{formatRelative(r.firstSeenAt)}</td>
                    <td className="mono" data-label="Sessions" style={{ textAlign: "right" }}>{r.sessions.toLocaleString()}</td>
                    <td data-label="Share">
                      <div className="rel-share">
                        <div className="viz" role="img" aria-label={`${r.release}: ${pct(share, 0)} of sessions`} style={{ height: 10, background: "var(--bg-elev-2)", borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ width: `${share}%`, height: "100%", background: "var(--viz-s1)", borderRadius: "0 4px 4px 0" }} />
                        </div>
                        <span className="mono small">{pct(share, 0)}</span>
                      </div>
                    </td>
                    <td className="mono" data-label="With errors" style={{ textAlign: "right" }}>{pct(r.errorRate, 2)}</td>
                    <td className="mono" data-label="New issues" style={{ textAlign: "right" }}>{r.newClusters}</td>
                    <td className="mono" data-label="LCP p75" style={{ textAlign: "right" }}>{ms(r.vitals.lcpP75)}</td>
                    <td className="mono" data-label="INP p75" style={{ textAlign: "right" }}>{ms(r.vitals.inpP75)}</td>
                    <td className="mono" data-label="CLS p75" style={{ textAlign: "right" }}>{r.vitals.clsP75?.toFixed(3) ?? "—"}</td>
                  </tr>
                  );
                })}
                {q.isPending && Array.from({ length: 4 }).map((_, i) => (
                  <tr key={`sk-${i}`} aria-hidden="true" className="static">
                    {Array.from({ length: 9 }).map((__, j) => (<td key={j}><span className="skeleton">loading</span></td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {releases.length >= 2 && (
            <Card title="Compare releases">
              <div className="ins-filters">
                <label>
                  Baseline
                  <select aria-label="Baseline release" value={relA?.release ?? ""} onChange={(e) => setA(e.target.value)} autoComplete="off">
                    {releases.map((r) => (<option key={r.release} value={r.release}>{r.release}</option>))}
                  </select>
                </label>
                <label>
                  Compare
                  <select aria-label="Compared release" value={relB?.release ?? ""} onChange={(e) => setB(e.target.value)} autoComplete="off">
                    {releases.map((r) => (<option key={r.release} value={r.release}>{r.release}</option>))}
                  </select>
                </label>
              </div>
              <table className="table releases-compare" aria-label="Release comparison">
                <thead><tr><th>Metric</th><th style={{ textAlign: "right" }}>{relA?.release}</th><th style={{ textAlign: "right" }}>{relB?.release}</th><th style={{ textAlign: "right" }}>Change</th></tr></thead>
                <tbody>
                  {deltas.map((d) => (
                    <tr key={d.metric} className="static" data-testid="delta-row">
                      <td>{d.metric}</td>
                      <td className="mono" data-label={relA?.release} style={{ textAlign: "right" }}>{d.format(d.a)}</td>
                      <td className="mono" data-label={relB?.release} style={{ textAlign: "right" }}>{d.format(d.b)}</td>
                      <td data-label="Change" style={{ textAlign: "right" }}><Delta value={d.delta} digits={d.pctDelta ? 1 : d.metric.startsWith("CLS") ? 3 : d.metric.includes("errors") ? 2 : 0} suffix={d.pctDelta ? "%" : d.suffix ?? ""} upIsGood={d.upIsGood} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}
    </>
  );
}
